import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';
import { isMissionsAuthError, isMissionsApiSuccess } from './missionsApi';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    json.http_status = res.status;
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authFetchWithRetry = async (path, options = {}) => {
  let res = await authFetch(path, options);
  if (isMissionsAuthError(res)) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    res = await authFetch(path, options);
  }
  return res;
};

export const isDailyMissionsApiSuccess = isMissionsApiSuccess;

export const getDailyMissionsPayload = (res) => res?.data ?? null;

export const getDailyMissionsErrorMessage = (res, fallback) => {
  if (isMissionsAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }
  const details = (Array.isArray(res?.data?.errors) ? res.data.errors : [])
    .map((error) => [error?.path, error?.message].filter(Boolean).join(': '))
    .filter(Boolean)
    .join(' ');
  const message = String(res?.message || '');
  if (details) return message ? `${message} ${details}` : details;
  return message || fallback;
};

/**
 * GET /admin/daily-missions
 * start_date and end_date are inclusive. from and to are the same bounds.
 * days is one page, newest mission_date first. occupied_dates is the full schedule.
 */
export const listDailyMissions = async ({ startDate, endDate, limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  const query = params.toString();
  return authFetchWithRetry(`/admin/daily-missions${query ? `?${query}` : ''}`, { method: 'GET' });
};

/** GET /admin/daily-missions/actions */
export const listDailyMissionActions = async () =>
  authFetchWithRetry('/admin/daily-missions/actions', { method: 'GET' });

/** GET /admin/daily-missions/:id */
export const getDailyMission = async (id) =>
  authFetchWithRetry(`/admin/daily-missions/${id}`, { method: 'GET' });

/** POST /admin/daily-missions — body is `{ override, missions }` */
export const createDailyMission = async (body) =>
  authFetch('/admin/daily-missions', { method: 'POST', body: JSON.stringify(body) });

/** PUT /admin/daily-missions/:id — partial; omit tasks to leave the task list unchanged */
export const updateDailyMission = async (id, body) =>
  authFetch(`/admin/daily-missions/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** DELETE /admin/daily-missions/:id */
export const deleteDailyMission = async (id) =>
  authFetch(`/admin/daily-missions/${id}`, { method: 'DELETE' });

function analyticsSearch(params = {}) {
  const search = new URLSearchParams();
  if (params.date) search.set('date', params.date);
  if (params.subjectType && params.subjectType !== 'all') search.set('subject_type', params.subjectType);
  if (params.tasksDone && params.tasksDone !== 'any') search.set('tasks_done', params.tasksDone);
  if (params.taskKey) search.set('task_key', params.taskKey);
  if (params.limit != null) search.set('limit', String(params.limit));
  if (params.offset) search.set('offset', String(params.offset));
  if (params.format) search.set('format', params.format);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** GET /admin/daily-missions/analytics */
export const getDailyMissionAnalytics = async (params = {}) =>
  authFetchWithRetry(`/admin/daily-missions/analytics${analyticsSearch(params)}`, { method: 'GET' });

/** GET /admin/daily-missions/analytics/participants */
export const listDailyMissionAnalyticsParticipants = async (params = {}) =>
  authFetchWithRetry(`/admin/daily-missions/analytics/participants${analyticsSearch(params)}`, {
    method: 'GET',
  });

/** GET /admin/daily-missions/analytics/participants?format=csv */
export const downloadDailyMissionAnalyticsCsv = async (params = {}) => {
  const token = getBearerToken();
  const res = await fetch(
    `${BASE_URL}/admin/daily-missions/analytics/participants${analyticsSearch({ ...params, format: 'csv' })}`,
    {
      credentials: 'include',
      headers: token ? { Authorization: token } : {},
    }
  );
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || 'Export failed');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `daily-mission-analytics-${params.date || 'day'}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
